import { FC, useMemo, useCallback } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { FilterChip } from "@/components/filters/FilterChip";
import { LightTooltip } from "@/components/LightTooltip";
import { useFilterDraft, useContextTypeLabel } from "@/hooks/filters";
import { ContextTypeCheckboxList } from "@/lib/company-filter/modules/intent/components/filters";
import { IntentTooltipContent } from "@/lib/company-filter/modules/intent/components";
import { INTENT_CONTEXT_TOOLTIP_SECTIONS } from "@/lib/company-filter/modules/intent/constants";
import { useIntentFiltersStorage } from "../hooks/useIntentFiltersStorage";

export const ContextTypeFilter: FC = () => {
  const { intentFilters, setIntentFilters } = useIntentFiltersStorage();

  const contextDraft = useFilterDraft(intentFilters.contextTypeIds, (value) =>
    setIntentFilters((prev) => ({ ...prev, contextTypeIds: value }))
  );

  const handleClear = useCallback(() => {
    setIntentFilters((prev) => ({ ...prev, contextTypeIds: [] }));
  }, [setIntentFilters]);

  const label = useContextTypeLabel(intentFilters.contextTypeIds);
  const count = intentFilters.contextTypeIds.length;

  const getSelectedCount = useCallback(() => count, [count]);

  const popoverOptions = useMemo(
    () => ({
      onClearButtonClick: contextDraft.clearDraft,
      onCancelButtonClick: contextDraft.cancelChanges,
      onApplyButtonClick: contextDraft.applyChanges,
      disableApplyButton: !contextDraft.hasChanges,
      disableCancelButton: false,
    }),
    [contextDraft]
  );

  const helpIcon = useMemo(
    () => (
      <LightTooltip
        placement="bottom"
        title={
          <IntentTooltipContent
            sections={INTENT_CONTEXT_TOOLTIP_SECTIONS}
            maxWidth={320}
          />
        }
      >
        <IconButton
          size="small"
          aria-label="Intent context help"
          disableRipple
          sx={{ p: 0.25 }}
        >
          <HelpOutlineIcon fontSize="inherit" sx={{ fontSize: 18 }} />
        </IconButton>
      </LightTooltip>
    ),
    []
  );

  return (
    <FilterChip
      label="Context"
      getSelectedCount={getSelectedCount}
      onDelete={count > 0 ? handleClear : undefined}
      renderLabel={() => (
        <Stack flexDirection="row" gap="3px">
          <Typography color="secondary">Context:</Typography>
          <Typography>{label}</Typography>
        </Stack>
      )}
      popoverOptions={popoverOptions}
      titleAdornment={helpIcon}
    >
      <Box id="intent-context-type-filter" mb={1}>
        <ContextTypeCheckboxList
          selectedIds={contextDraft.draft}
          onChange={contextDraft.updateDraft}
        />
      </Box>
    </FilterChip>
  );
};
